import React, { useEffect, useMemo, useRef, useState } from "react";

/* ---------- Utils ---------- */
const formatBytes = (bytes = 0) => {
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  if (!bytes) return "0 B";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const v = bytes / 1024 ** i;
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${sizes[i]}`;
};
const currency = (n = 0, c = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: c }).format(n);
const clamp = (n, min = 0, max = 100) => Math.max(min, Math.min(max, n));

/* Smooth % animation */
function useAnimatedProgress(targetPercent, duration = 1200) {
  const [progress, setProgress] = useState(0);
  const req = useRef();
  const start = useRef();
  useEffect(() => {
    const step = (t) => {
      if (!start.current) start.current = t;
      const e = t - start.current;
      const p = Math.min((e / duration) * targetPercent, targetPercent);
      setProgress(p);
      if (e < duration) req.current = requestAnimationFrame(step);
    };
    req.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(req.current);
  }, [targetPercent, duration]);
  return progress;
}

/* Count-up animation */
function useCountUp(target = 0, duration = 800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const loop = (t) => {
      const k = Math.min((t - start) / duration, 1);
      setVal(Math.round(target * (0.6 + 0.4 * k)));
      if (k < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

/* Circular gauge (SVG) – light: card trắng; dark: card tối như cũ */
function Gauge({ value = 0, max = 100, title = "", unit = "", color = "#6366f1" }) {
  const pct = max > 0 ? clamp((value / max) * 100) : 0;
  const animated = useAnimatedProgress(pct);
  const R = 46;
  const C = 2 * Math.PI * R;
  const dash = C - (C * animated) / 100;
  return (
    <div
      className="
        relative w-40 h-40 rounded-2xl p-4 overflow-hidden flex items-center justify-center
        bg-white text-gray-900 shadow-md ring-1 ring-black/5
        dark:bg-black/40 dark:text-white dark:border dark:border-white/10 dark:backdrop-blur-md dark:shadow-lg dark:ring-0
      "
    >
      <svg viewBox="0 0 100 100" className="absolute inset-0">
        <circle cx="50" cy="50" r={R} stroke="#e5e7eb" strokeWidth="10" fill="none" />
        <circle
          cx="50"
          cy="50"
          r={R}
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeDasharray={C}
          strokeDashoffset={dash}
          strokeLinecap="round"
        />
      </svg>
      <div className="relative text-center">
        <div className="text-3xl font-bold">{animated.toFixed(0)}%</div>
        <div className="text-xs text-gray-600 dark:text-white/70 mt-1">{title}</div>
        {unit && <div className="text-[10px] text-gray-500 dark:text-white/60 mt-1">{unit}</div>}
      </div>
    </div>
  );
}

/* Tiny sparkline */
function Sparkline({ data = [], width = 220, height = 60, stroke = "#22d3ee" }) {
  if (!data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ---------- Mock Data ---------- */
const MOCK = {
  fleet: { total: 28, running: 22, stopped: 3, issues: 3 },
  billing: {
    monthToDate: 842.12,
    projected: 1120.0,
    unpaidInvoices: 2,
    unpaidAmount: 264.33,
    lastInvoiceId: "INV-2043",
  },
  uptime: { last30d: 99.95, sla: 99.9 },
  security: { passRate: 92, patchCompliance: 88, criticalFindings: 1 },
  capacity: {
    cpu: { used: 148, total: 200 }, // vCPU
    ram: { used: 7.8 * 1024 ** 3, total: 12 * 1024 ** 3 }, // bytes
    storage: { used: 7.2 * 1024 ** 4, total: 12 * 1024 ** 4 }, // bytes
  },
  costSeries: Array.from({ length: 20 }).map((_, i) => 40 + i * 3 + Math.sin(i / 2) * 6 + Math.random() * 5),
};

/* ---------- Main Section ---------- */
export default function ClientKPISection() {
  const [seed, setSeed] = useState(0);
  const data = useMemo(() => ({ ...MOCK }), [seed]);

  const totalVM = useCountUp(data.fleet.total);
  const runningVM = useCountUp(data.fleet.running);
  const unpaidCnt = useCountUp(data.billing.unpaidInvoices);

  const cpuPct = (data.capacity.cpu.used / data.capacity.cpu.total) * 100;
  const ramPct = (data.capacity.ram.used / data.capacity.ram.total) * 100;
  const diskPct = (data.capacity.storage.used / data.capacity.storage.total) * 100;

  return (
    <section className="col-span-5 px-4 sm:px-0 mt-10 space-y-8">
      {/* Top KPIs – light: thẻ trắng; dark: thẻ tối cũ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="
            rounded-2xl p-5 transition
            bg-white text-gray-900 shadow-md ring-1 ring-black/5
            hover:bg-white
            dark:bg-black/40 dark:text-white dark:border dark:border-white/10 dark:backdrop-blur-md dark:shadow-lg dark:ring-0 dark:hover:bg-black/50
          "
        >
          <div className="text-xs text-gray-600 dark:text-white/60">Total VMs</div>
          <div className="mt-1 text-3xl font-semibold">{totalVM}</div>
          <div className="mt-2 text-xs text-gray-600 dark:text-white/60">
            Running {runningVM} • Issues {data.fleet.issues}
          </div>
        </div>

        <div
          className="
            rounded-2xl p-5 transition
            bg-white text-gray-900 shadow-md ring-1 ring-black/5
            hover:bg-white
            dark:bg-black/40 dark:text-white dark:border dark:border-white/10 dark:backdrop-blur-md dark:shadow-lg dark:ring-0 dark:hover:bg-black/50
          "
        >
          <div className="text-xs text-gray-600 dark:text-white/60">Unpaid invoices</div>
          <div className="mt-1 text-3xl font-semibold">{unpaidCnt}</div>
          <div className="mt-2 text-xs text-gray-600 dark:text-white/60">
            Outstanding {currency(data.billing.unpaidAmount)}
          </div>
        </div>

        <div
          className="
            rounded-2xl p-5 transition
            bg-white text-gray-900 shadow-md ring-1 ring-black/5
            hover:bg-white
            dark:bg-black/40 dark:text-white dark:border dark:border-white/10 dark:backdrop-blur-md dark:shadow-lg dark:ring-0 dark:hover:bg-black/50
          "
        >
          <div className="text-xs text-gray-600 dark:text-white/60">MTD Spend</div>
          <div className="mt-1 text-3xl font-semibold">{currency(data.billing.monthToDate)}</div>
          <div className="mt-2 h-[60px] rounded-xl p-2 bg-gray-100 dark:bg-white/5">
            <Sparkline data={data.costSeries} />
          </div>
          <div className="mt-2 text-xs text-gray-600 dark:text-white/60">
            Projected {currency(data.billing.projected)}
          </div>
        </div>

        <div
          className="
            rounded-2xl p-5 transition
            bg-white text-gray-900 shadow-md ring-1 ring-black/5
            hover:bg-white
            dark:bg-black/40 dark:text-white dark:border dark:border-white/10 dark:backdrop-blur-md dark:shadow-lg dark:ring-0 dark:hover:bg-black/50
          "
        >
          <div className="text-xs text-gray-600 dark:text-white/60">Uptime (30d)</div>
          <div className="mt-1 text-3xl font-semibold">{data.uptime.last30d.toFixed(2)}%</div>
          <div className="mt-2 w-full h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-white/10">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${clamp((data.uptime.last30d / data.uptime.sla) * 100)}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-gray-600 dark:text-white/60">SLA {data.uptime.sla}%</div>
        </div>
      </div>

      {/* Gauges row (ví dụ) */}
      
    </section>
  );
}
